import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

/**
 * Country → state/province map for the building address form.
 * Matches the V1 getStatesByCountry utility.
 */
const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
];

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
  ['DC','District of Columbia'],
];

const CA_PROVINCES = [
  ['AB','Alberta'],['BC','British Columbia'],['MB','Manitoba'],['NB','New Brunswick'],
  ['NL','Newfoundland and Labrador'],['NS','Nova Scotia'],['NT','Northwest Territories'],
  ['NU','Nunavut'],['ON','Ontario'],['PE','Prince Edward Island'],['QC','Quebec'],
  ['SK','Saskatchewan'],['YT','Yukon'],
];

function getStatesByCountry(country) {
  if (country === 'CA') return CA_PROVINCES;
  return US_STATES;
}

/**
 * BuildingDrawer — slide-out drawer for Add / Edit building.
 * Includes address fields and a searchable area picker.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   building: object | null,        // null = Add mode, object = Edit mode
 *   onSave: (data: object) => void,
 *   onDelete: (building: object) => void,
 *   areas: Array<{ area_id: number, area_name: string, area_type_description: string }>
 * }} props
 */
const BuildingDrawer = ({ isOpen, onClose, building, onSave, onDelete, areas }) => {
  const isEditMode = !!building;
  const drawerRef = useRef(null);

  // Form state
  const [buildingName, setBuildingName] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [country, setCountry] = useState('US');
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [areaSearch, setAreaSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // State/province list based on country
  const stateOptions = useMemo(() => getStatesByCountry(country), [country]);

  // Populate form on open/building change
  useEffect(() => {
    if (isOpen && building) {
      setBuildingName(building.building_name || '');
      setStreetAddress(building.street_address || '');
      setCity(building.city || '');
      setCountry(building.country || 'US');
      setState(building.state || '');
      setZipcode(building.zipcode || '');
      setSelectedAreaIds(
        (building.areas || []).map((a) => a.area_id?.toString() || a.toString())
      );
      setAreaSearch('');
    } else if (isOpen && !building) {
      setBuildingName('');
      setStreetAddress('');
      setCity('');
      setState('');
      setZipcode('');
      setCountry('US');
      setSelectedAreaIds([]);
      setAreaSearch('');
    }
  }, [isOpen, building]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Filter areas by search
  const filteredAreas = useMemo(() => {
    if (!areaSearch.trim()) return areas || [];
    const s = areaSearch.toLowerCase();
    return (areas || []).filter(
      (a) =>
        a.area_name?.toLowerCase().includes(s) ||
        a.area_type_description?.toLowerCase().includes(s)
    );
  }, [areas, areaSearch]);

  const handleAreaToggle = useCallback((areaId) => {
    const aStr = areaId.toString();
    setSelectedAreaIds((prev) =>
      prev.includes(aStr) ? prev.filter((id) => id !== aStr) : [...prev, aStr]
    );
  }, []);

  const handleSelectAllAreas = useCallback(() => {
    const allFilteredIds = filteredAreas.map((a) => a.area_id.toString());
    setSelectedAreaIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
  }, [filteredAreas]);

  const handleClearAreas = useCallback(() => {
    setSelectedAreaIds([]);
  }, []);

  const handleCountryChange = useCallback((newCountry) => {
    setCountry(newCountry);
    setState(''); // reset state when country changes
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSaving(true);

      const formData = {
        building_id: building?.building_id || null,
        building_name: buildingName.trim(),
        street_address: streetAddress.trim(),
        city: city.trim(),
        state,
        zipcode: zipcode.trim(),
        country,
        area_ids: selectedAreaIds.map((id) => parseInt(id, 10)),
      };

      try {
        await onSave(formData);
      } finally {
        setSaving(false);
      }
    },
    [buildingName, streetAddress, city, state, zipcode, country, selectedAreaIds, building, onSave]
  );

  /* Hands off to the page's typed confirmation — see AreaDrawer for why the
     inline footer confirmation was removed. */
  const handleDeleteClick = useCallback(() => {
    if (building && onDelete) onDelete(building);
  }, [building, onDelete]);

  const isFormValid = buildingName.trim();

  return (
    <>
      {/* Backdrop */}
      {isOpen ? (
        <div className="ap-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      ) : null}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`ap-drawer ap-drawer--lg ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? 'Edit Building' : 'Add Building'}
      >
        {/* Header */}
        <div className="ap-drawer__header">
          <h2 className="ap-drawer__title">
            {isEditMode ? 'Edit Building' : 'Add Building'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="ap-drawer__close"
            aria-label="Close drawer"
          >
            <svg className="ap-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable form body */}
        <form onSubmit={handleSubmit} className="ap-form">
          <div className="ap-form__body">
            {/* Building Name */}
            <div>
              <label htmlFor="drawer-building-name" className="ap-field__label">
                Building Name <span className="ap-field__required">*</span>
              </label>
              <input
                type="text"
                id="drawer-building-name"
                value={buildingName}
                onChange={(e) => setBuildingName(e.target.value)}
                required
                className="ap-field__input"
                placeholder="Enter building name"
              />
            </div>

            {/* Street Address */}
            <div>
              <label htmlFor="drawer-street-address" className="ap-field__label">
                Street Address
              </label>
              <input
                type="text"
                id="drawer-street-address"
                value={streetAddress}
                onChange={(e) => setStreetAddress(e.target.value)}
                className="ap-field__input"
                placeholder="123 Main St"
              />
            </div>

            {/* City & State */}
            <div className="ap-form-grid">
              <div>
                <label htmlFor="drawer-city" className="ap-field__label">City</label>
                <input
                  type="text"
                  id="drawer-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="ap-field__input"
                  placeholder="City"
                />
              </div>
              <div>
                <label htmlFor="drawer-state" className="ap-field__label">State / Province</label>
                <select
                  id="drawer-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="ap-field__select"
                >
                  <option value="">Select...</option>
                  {stateOptions.map(([abbr, name]) => (
                    <option key={abbr} value={abbr}>{name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Zip & Country */}
            <div className="ap-form-grid">
              <div>
                <label htmlFor="drawer-zipcode" className="ap-field__label">Zip / Postal Code</label>
                <input
                  type="text"
                  id="drawer-zipcode"
                  value={zipcode}
                  onChange={(e) => setZipcode(e.target.value)}
                  className="ap-field__input"
                  placeholder={country === 'CA' ? 'A1A 1A1' : '12345'}
                />
              </div>
              <div>
                <label htmlFor="drawer-country" className="ap-field__label">Country</label>
                <select
                  id="drawer-country"
                  value={country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  className="ap-field__select"
                >
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Area Picker */}
            <div className="ap-area-picker">
              <div className="ap-area-picker__header">
                <p className="ap-field__label" style={{ marginBottom: 0 }}>
                  Areas
                  <span className="ap-area-picker__count">
                    ({selectedAreaIds.length} selected)
                  </span>
                </p>
              </div>

              {/* Search */}
              <div className="ap-area-picker__search">
                <i className="fe fe-search ap-area-picker__search-icon" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Search areas..."
                  value={areaSearch}
                  onChange={(e) => setAreaSearch(e.target.value)}
                  className="ap-area-picker__search-input"
                />
              </div>

              {/* Select All / Clear */}
              <div className="ap-area-picker__actions">
                <button
                  type="button"
                  onClick={handleSelectAllAreas}
                  className="ap-area-picker__action-btn"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleClearAreas}
                  className="ap-area-picker__action-btn"
                >
                  Clear
                </button>
              </div>

              {/* Area list */}
              <div className="ap-area-picker__list">
                {filteredAreas.length === 0 ? (
                  <p className="ap-area-picker__empty">No areas found</p>
                ) : (
                  filteredAreas.map((a) => {
                    const isChecked = selectedAreaIds.includes(a.area_id.toString());
                    return (
                      <label
                        key={a.area_id}
                        className={`ap-area-picker__item ${isChecked ? 'is-selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleAreaToggle(a.area_id)}
                          className="ap-checkbox"
                        />
                        <div className="ap-area-picker__item-info">
                          <span className="ap-area-picker__item-name">{a.area_name}</span>
                          <span className="ap-area-picker__item-type">
                            {a.area_type_description || ''}
                          </span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Footer with actions */}
          <div className="ap-form__footer">
            {/* Delete button (edit mode only) — opens the page's typed
                confirmation rather than confirming here. */}
            {isEditMode ? (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="ap-btn ap-btn--danger"
              >
                <i className="fe fe-trash-2" aria-hidden="true" />
                Delete
              </button>
            ) : null}

            {/* Spacer pushes Cancel/Save right */}
            <div style={{ flex: 1 }} />

            <button
              type="button"
              onClick={onClose}
              className="ap-btn ap-btn--cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || saving}
              className="ap-btn ap-btn--primary"
            >
              {saving ? (
                <span className="ap-saving-text">
                  <svg className="ap-icon-md ap-spinner" fill="none" viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </span>
              ) : (
                isEditMode ? 'Save Changes' : 'Add Building'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default BuildingDrawer;
